const getSandboxState = (organization) => organization?.sandbox || organization?.Sandbox || {};

const SANDBOX_DEMO_CLIENT_NAMES = new Set([
    'luna labor',
    'betty breech',
    'twila twins',
    'cheri cervix',
    'eliana effacement',
]);

export function isSandboxOrganization(organization) {
    const accountMode = organization?.accountMode || organization?.AccountMode;
    const sandbox = getSandboxState(organization);

    return accountMode === 'Sandbox' || sandbox.isSandbox === true || sandbox.IsSandbox === true;
}

export function getSandboxDaysRemaining(organization) {
    const sandbox = getSandboxState(organization);
    const value =
        sandbox.daysRemaining ??
        sandbox.DaysRemaining ??
        sandbox.fullAccessDaysRemaining ??
        organization?.sandboxDaysRemaining;

    return Number.isFinite(value) ? value : null;
}

export function isSandboxReadOnly(organization) {
    const sandbox = getSandboxState(organization);

    return sandbox.isReadOnly === true || sandbox.readOnly === true || sandbox.IsReadOnly === true;
}

function isSandboxLikeValue(value) {
    return String(value || '').toLowerCase() === 'sandbox';
}

function hasSandboxFlag(value) {
    return value?.isSandbox === true || value?.IsSandbox === true;
}

function getField(record, camelName, pascalName = camelName.charAt(0).toUpperCase() + camelName.slice(1)) {
    return record?.[camelName] ?? record?.[pascalName];
}

export function isSandboxUser(record) {
    if (!record) return false;

    if (
        isSandboxLikeValue(getField(record, 'accountMode')) ||
        isSandboxLikeValue(getField(record, 'organizationAccountMode')) ||
        isSandboxLikeValue(getField(record, 'orgAccountMode')) ||
        isSandboxLikeValue(record.organization?.accountMode || record.Organization?.AccountMode) ||
        hasSandboxFlag(getField(record, 'sandbox')) ||
        hasSandboxFlag(getField(record, 'organizationSandbox')) ||
        hasSandboxFlag(record.organization?.sandbox || record.Organization?.Sandbox)
    ) {
        return true;
    }

    const firstName = String(getField(record, 'firstName') || '').trim();
    const lastName = String(getField(record, 'lastName') || '').trim();
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
    if (SANDBOX_DEMO_CLIENT_NAMES.has(fullName)) return true;

    const email = String(getField(record, 'email') || '').toLowerCase();
    if (email.endsWith('@example.invalid')) return true;

    const phoneValues = [
        getField(record, 'mobilePhone'),
        getField(record, 'homePhone'),
        getField(record, 'phoneNumber'),
    ];
    if (phoneValues.some((value) => String(value || '').startsWith('555-011'))) return true;

    const searchable = [
        email,
        firstName,
        lastName,
        getField(record, 'organizationName'),
        record.organization?.name,
        getField(record, 'street'),
        getField(record, 'city'),
        getField(record, 'county'),
    ].join(' ');

    if (/\bdemo client lane\b/i.test(searchable)) return true;
    if (/\bfaketown\b/i.test(searchable)) return true;
    if (/\bcounty\s+demo\b/i.test(searchable) || String(getField(record, 'county') || '').toLowerCase() === 'demo') return true;

    return /(^|[._+\-\s@])(sandbox|demo|test)([._+\-\s@]|$)/i.test(searchable);
}

export const SANDBOX_STRIPE_CONNECT_MESSAGE =
    'Stripe Connect is not available in sandbox mode. Start your practice to accept online payments from clients.';

export const SANDBOX_FAX_REQUEST_MESSAGE =
    'Fax number requests are not available in sandbox mode. Start your practice to request a new fax number or port an existing one.';

export const SANDBOX_BAA_MESSAGE =
    'Business Associate Agreement signing is not available in sandbox mode. Start your practice to sign the required HIPAA agreement.';

export function getSandboxBannerMessage(organization) {
    if (isSandboxReadOnly(organization)) {
        return 'Your demo practice is now read-only. Start your practice to continue working in Sena.';
    }

    const daysRemaining = getSandboxDaysRemaining(organization);
    if (daysRemaining !== null) {
        return `You're exploring a demo practice. Full access ends in ${daysRemaining} days.`;
    }

    return "You're exploring a demo practice. Changes you make here only affect your sandbox.";
}
